import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { Subscription } from 'rxjs';
import { NgxSpinnerService } from 'ngx-spinner';
import { Router, ActivatedRoute } from '@angular/router';
import { DataExchangeRequestApiService } from 'src/app/data-transfer/services/data-exchange-request-api-service';
import { DataExchangeRequestDto } from 'src/app/data-transfer/entities/data-exchange-request-entities/data-exchange-request-dto';
import { DataExchangeRequestStatusDto } from 'src/app/data-transfer/entities/data-exchange-request-entities/data-exchange-request-status-dto';

@Component({
  selector: 'app-data-exchange-requests',
  templateUrl: './data-exchange-requests.component.html',
  styleUrls: ['./data-exchange-requests.component.scss'],
})
export class DataExchangeRequestsComponent implements OnInit, OnDestroy {
  readonly displayedColumns: string[] = ['expand', 'id', 'name', 'counterpart', 'subRequestCount', 'createdAt', 'status', 'actions'];

  readonly systemColumns = [
    'id',
    'brandName',
    'productName',
    'articleNumber',
    'subRequestStatus',
    'lastModifiedAt',
    'showSubRequestComments',
    'approveSubRequest',
    'navigateToSubRequest',
  ];
  readonly unitColumns = [
    'id',
    'packagingTypeName',
    'brandName',
    'productName',
    'articleNumber',
    'subRequestStatus',
    'lastModifiedAt',
    'showSubRequestComments',
    'approveSubRequest',
    'navigateToSubRequest',
  ];
  readonly componentColumns = [
    'id',
    'packagingComponentCategoryName',
    'articleName',
    'articleNumber',
    'subRequestStatus',
    'lastModifiedAt',
    'showSubRequestComments',
    'approveSubRequest',
    'navigateToSubRequest',
  ];
  readonly materialColumns = [
    'id',
    'articleName',
    'articleNumber',
    'manufacturerName',
    'subRequestStatus',
    'lastModifiedAt',
    'showSubRequestComments',
    'approveSubRequest',
    'navigateToSubRequest',
  ];

  readonly systemColumnsReceived = ['id', 'brandName', 'productName', 'articleNumber', 'subRequestStatus', 'lastModifiedAt', 'showSubRequestComments', 'navigateToSubRequest'];
  readonly unitColumnsReceived = [
    'id',
    'packagingTypeName',
    'brandName',
    'productName',
    'articleNumber',
    'subRequestStatus',
    'lastModifiedAt',
    'showSubRequestComments',
    'navigateToSubRequest',
  ];
  readonly componentColumnsReceived = [
    'id',
    'packagingComponentCategoryName',
    'articleName',
    'articleNumber',
    'subRequestStatus',
    'lastModifiedAt',
    'showSubRequestComments',
    'navigateToSubRequest',
  ];
  readonly materialColumnsReceived = [
    'id',
    'articleName',
    'articleNumber',
    'manufacturerName',
    'subRequestStatus',
    'lastModifiedAt',
    'showSubRequestComments',
    'navigateToSubRequest',
  ];

  selectedTabIndex = 0;
  showClosedAndDeleted = false;

  sentDataSource = new MatTableDataSource<DataExchangeRequestDto>();
  receivedDataSource = new MatTableDataSource<DataExchangeRequestDto>();
  closedSentDataSource = new MatTableDataSource<DataExchangeRequestDto>();
  closedReceivedDataSource = new MatTableDataSource<DataExchangeRequestDto>();

  closedSentLoading = false;
  closedReceivedLoading = false;
  closedLoaded = false;

  private allSentRequests: DataExchangeRequestDto[] = [];
  private allReceivedRequests: DataExchangeRequestDto[] = [];
  private sentSubscription?: Subscription;
  private receivedSubscription?: Subscription;
  private closedSentSubscription?: Subscription;
  private closedReceivedSubscription?: Subscription;
  private queryParamsSubscription?: Subscription;

  requestIdToExpand: number | null = null;

  constructor(
    private dataExchangeRequestApiService: DataExchangeRequestApiService,
    private spinner: NgxSpinnerService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.queryParamsSubscription = this.route.queryParams.subscribe(params => {
      this.selectedTabIndex = +(params['tab'] ?? 0);
      this.requestIdToExpand = params['requestId'] ? +params['requestId'] : null;
    });
    this.loadData();
  }

  loadData(): void {
    this.sentSubscription?.unsubscribe();
    this.receivedSubscription?.unsubscribe();
    this.spinner.show();
    this.sentSubscription = this.dataExchangeRequestApiService.getSentRequests(true).subscribe({
      next: requests => {
        this.allSentRequests = requests;
        this.applyDataFilter();
        this.trySelectTabForRequestId();
        this.spinner.hide();
      },
      error: () => this.spinner.hide(),
    });
    this.receivedSubscription = this.dataExchangeRequestApiService.getReceivedRequests(true).subscribe({
      next: requests => {
        this.allReceivedRequests = requests;
        this.applyDataFilter();
        this.trySelectTabForRequestId();
        this.spinner.hide();
      },
      error: () => this.spinner.hide(),
    });

    this.resetDeletedData();
    if (this.showClosedAndDeleted) {
      this.loadDeletedData();
    }
  }

  private trySelectTabForRequestId(): void {
    if (this.requestIdToExpand == null) {
      return;
    }
    if (this.allSentRequests.some(r => r.id === this.requestIdToExpand)) {
      this.selectedTabIndex = 0;
    } else if (this.allReceivedRequests.some(r => r.id === this.requestIdToExpand)) {
      this.selectedTabIndex = 1;
    }
  }

  toggleShowClosedAndDeleted(): void {
    this.showClosedAndDeleted = !this.showClosedAndDeleted;
    if (this.showClosedAndDeleted) {
      this.loadDeletedData();
    }
    this.applyDataFilter();
  }

  onTabChange(index: number): void {
    this.selectedTabIndex = index;
    this.router.navigate([], { queryParams: { tab: index }, queryParamsHandling: 'merge', replaceUrl: true });
  }

  private applyDataFilter(): void {
    const hidden = [DataExchangeRequestStatusDto.Closed];
    const process = (requests: DataExchangeRequestDto[]) => {
      const filtered = this.showClosedAndDeleted ? requests : requests.filter(r => !hidden.includes(r.status));
      return filtered.sort((a, b) => a.status - b.status || a.id - b.id);
    };
    this.sentDataSource = new MatTableDataSource(process(this.allSentRequests));
    this.receivedDataSource = new MatTableDataSource(process(this.allReceivedRequests));
  }

  private loadDeletedData(): void {
    if (this.closedLoaded || this.closedSentLoading || this.closedReceivedLoading) {
      return;
    }

    this.closedSentSubscription?.unsubscribe();
    this.closedReceivedSubscription?.unsubscribe();

    this.closedSentLoading = true;
    this.closedReceivedLoading = true;

    this.closedSentSubscription = this.dataExchangeRequestApiService.getClosedSentRequests(true).subscribe({
      next: requests => {
        this.closedSentDataSource = new MatTableDataSource(this.sortDeletedRequests(requests));
        this.closedSentLoading = false;
        this.updateDeletedLoadedFlag();
      },
      error: () => {
        this.closedSentLoading = false;
        this.updateDeletedLoadedFlag();
      },
    });
    this.closedReceivedSubscription = this.dataExchangeRequestApiService.getClosedReceivedRequests(true).subscribe({
      next: requests => {
        this.closedReceivedDataSource = new MatTableDataSource(this.sortDeletedRequests(requests));
        this.closedReceivedLoading = false;
        this.updateDeletedLoadedFlag();
      },
      error: () => {
        this.closedReceivedLoading = false;
        this.updateDeletedLoadedFlag();
      },
    });
  }

  private sortDeletedRequests(requests: DataExchangeRequestDto[]): DataExchangeRequestDto[] {
    return [...requests].sort((a, b) => a.id - b.id);
  }

  private updateDeletedLoadedFlag(): void {
    this.closedLoaded = !this.closedSentLoading && !this.closedReceivedLoading;
  }

  private resetDeletedData(): void {
    this.closedSentSubscription?.unsubscribe();
    this.closedReceivedSubscription?.unsubscribe();
    this.closedSentDataSource = new MatTableDataSource<DataExchangeRequestDto>();
    this.closedReceivedDataSource = new MatTableDataSource<DataExchangeRequestDto>();
    this.closedSentLoading = false;
    this.closedReceivedLoading = false;
    this.closedLoaded = false;
  }

  ngOnDestroy(): void {
    this.queryParamsSubscription?.unsubscribe();
    this.sentSubscription?.unsubscribe();
    this.receivedSubscription?.unsubscribe();
    this.closedSentSubscription?.unsubscribe();
    this.closedReceivedSubscription?.unsubscribe();
  }
}
